import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const CONSENT_KEY = "ff_cookie_consent";
const CONSENT_EXPIRY_DAYS = 180;
const GA4_ID = "G-V7GGJ77XFS";

function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() > data.expires) { localStorage.removeItem(CONSENT_KEY); return null; }
    return data;
  } catch { return null; }
}

function setConsent(preferences) {
  const data = { ...preferences, timestamp: Date.now(), expires: Date.now() + CONSENT_EXPIRY_DAYS * 86400000 };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
}

function loadGA4() {
  if (document.querySelector(`script[src*="gtag/js"]`)) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA4_ID);
}

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, affiliate: true });

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      if (existing.analytics) loadGA4();
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, []);

  const accept = () => {
    setConsent({ analytics: true, affiliate: true });
    loadGA4();
    setVisible(false);
  };

  const reject = () => {
    setConsent({ analytics: false, affiliate: false });
    setVisible(false);
  };

  const savePrefs = () => {
    setConsent(prefs);
    if (prefs.analytics) loadGA4();
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-[#111111] shadow-[0_-2px_10px_rgba(0,0,0,0.05)]" data-testid="cookie-consent">
      {!showPrefs ? (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
          <p className="text-sm text-slate-600 dark:text-slate-400 flex-1 leading-relaxed">
            We use cookies to analyze traffic and improve your experience. You can accept or manage your preferences.{" "}
            <Link to="/privacy-policy" className="text-[#1E88E5] hover:underline">Privacy Policy</Link>
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={accept} className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium px-5 py-2 rounded-lg hover:bg-slate-800 dark:hover:bg-gray-100 transition-colors" data-testid="cookie-accept">
              Accept All
            </button>
            <button onClick={reject} className="border border-gray-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium px-5 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors" data-testid="cookie-reject">
              Reject Non-Essential
            </button>
            <button onClick={() => setShowPrefs(true)} className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white underline underline-offset-2 px-2 py-2 transition-colors" data-testid="cookie-manage">
              Manage
            </button>
          </div>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Cookie Preferences</p>
            <button onClick={() => setShowPrefs(false)} className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">Close</button>
          </div>
          <div className="space-y-2 mb-3">
            <label className="flex items-center justify-between py-1">
              <span className="text-sm text-slate-600 dark:text-slate-400">Essential (always active)</span>
              <span className="text-xs text-emerald-500 font-medium">Required</span>
            </label>
            <label className="flex items-center justify-between py-1 cursor-pointer">
              <span className="text-sm text-slate-600 dark:text-slate-400">Analytics (Google Analytics)</span>
              <input type="checkbox" checked={prefs.analytics} onChange={e => setPrefs(p => ({ ...p, analytics: e.target.checked }))} className="rounded border-gray-300 dark:border-slate-600 text-[#1E88E5] focus:ring-[#1E88E5]" />
            </label>
            <label className="flex items-center justify-between py-1 cursor-pointer">
              <span className="text-sm text-slate-600 dark:text-slate-400">Affiliate Tracking</span>
              <input type="checkbox" checked={prefs.affiliate} onChange={e => setPrefs(p => ({ ...p, affiliate: e.target.checked }))} className="rounded border-gray-300 dark:border-slate-600 text-[#1E88E5] focus:ring-[#1E88E5]" />
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={savePrefs} className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium px-5 py-2 rounded-lg" data-testid="cookie-save-prefs">Save Preferences</button>
            <button onClick={accept} className="text-sm text-[#1E88E5] hover:underline px-3 py-2">Accept All</button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Check if user consented to a specific category */
export const hasConsent = (category) => {
  const consent = getConsent();
  if (!consent) return false;
  return consent[category] === true;
};
